/* eslint-disable no-underscore-dangle */

// This file was ported from AdsAppUI repo.

// / Supported expression(s):
// / * * * * * *
// / - - - - - -
// / | | | | | |
// / | | | | | +----- year (1 - 9999)
// / | | | | +----- day of week (0 - 6) (Sunday=0)
// / | | | +------- month (1 - 12)
// / | | +--------- day of month (1 - 31)
// / | +----------- hour (0 - 23)
// / +------------- min (0 - 59)
// /
// / Valid scenarios:
// / a. "1 2 07 01 * 2015": Run at "07/01/2015 02:01"
// / b. "0 16 * * * *": Run daily at 4:00 pm
// / c. "0 16 * * 3 *": Run weekly, every Wed at 4:00 pm
// / d. "0 16 1 * * *": Run monthly, every 1st of the month
// / Note: If the year is not specified (i.e. "0 16 1 * *"), the year is defaulted to any.

import _ from 'underscore';
import { parseExpressionSync } from 'cron-parser';
import { curlyTemplate } from '@bingads-webui-universal/primitive-utilities';

/**
 * Enumerates possible schedule frequencies.
 */
export const frequency = {
  NONE: -1,
  DAILY: 0,
  WEEKLY: 1,
  MONTHLY: 2,
  ONCE: 3,
  HOURLY: 4,
  EVERY_6_HOURS: 5,
  EVERY_12_HOURS: 6,
};

function isEqualInterval(range, interval, values) {
  if (_.size(values) < 2) return false;
  const valuesToNextRange = [...values, range + values[0]];
  for (let i = 0; i < valuesToNextRange.length - 1; i += 1) {
    if (valuesToNextRange[i + 1] - valuesToNextRange[i] !== interval) {
      return false;
    }
  }
  return true;
}

function getFrequency(cron) {
  if (cron.year.length > 1) {
    // run once, year value is not *
    return frequency.ONCE;
  } else if (cron._fields.dayOfMonth.length === 1) {
    // monthly
    return frequency.MONTHLY;
  } else if (cron._fields.dayOfWeek.length === 1) {
    // Weekly
    return frequency.WEEKLY;
  } else if (cron._fields.hour.length === 1) {
    // daily
    return frequency.DAILY;
  } else if (cron._fields.minute.length === 1 &&
    cron._fields.hour.length === 4 &&
    isEqualInterval(24, 6, cron._fields.hour)) {
    // every 6-hour
    return frequency.EVERY_6_HOURS;
  } else if (cron._fields.minute.length === 1 &&
    cron._fields.hour.length === 2 &&
    isEqualInterval(24, 12, cron._fields.hour)) {
    // every 12-hour
    return frequency.EVERY_12_HOURS;
  }

  // hourly
  return frequency.HOURLY;
}


// Input string is expected to have year component init
// we strip the year component first and pass the rest of
// the standard cron string to the library
function init(cronString) {
  if (!cronString) {
    return null;
  }

  const tokens = cronString.split(' ');
  let year;
  let modifiedCronString = cronString;

  if (tokens.length > 5) {
    // Year is added to the cron string
    year = _.last(tokens);
    modifiedCronString = _.initial(tokens).join(' ');
  }

  // adding padding for seconds, which is required by the 3rd-party library cron-parser.
  modifiedCronString = `* ${modifiedCronString}`;

  // eslint-disable-next-line
  const cronResult = parseExpressionSync(modifiedCronString);

  cronResult.year = year;

  return cronResult;
}

/**
 * Parse a cron string into a cron model.
 *
 * @param {String} cronString - a raw cron string.
 * @returns {Object} a cron model which may contain fields min, hour, dayOfWeek, dayOfMonth,
 * month, and year.
 */
export function parse(cronString) {
  if (!cronString) {
    throw new Error(`Empty cron string: ${cronString}`);
  }

  const cron = init(cronString);

  if (!cron) {
    throw new Error(`Unable to parse cron string: ${cronString}`);
  }

  const {
    minute,
    hour,
    dayOfWeek,
    dayOfMonth,
    month,
  } = cron._fields;

  const cronModel = {
    frequency: getFrequency(cron),
  };

  switch (cronModel.frequency) {
    case frequency.ONCE:
      cronModel.year = parseInt(cron.year, 10);
      [cronModel.month] = month;
      [cronModel.dayOfMonth] = dayOfMonth;
      [cronModel.hour] = hour;
      break;
    case frequency.MONTHLY:
      [cronModel.dayOfMonth] = dayOfMonth;
      [cronModel.hour] = hour;
      break;
    case frequency.WEEKLY:
      [cronModel.dayOfWeek] = dayOfWeek;
      [cronModel.hour] = hour;
      break;
    case frequency.DAILY:
      [cronModel.hour] = hour;
      break;
    case frequency.HOURLY:
      [cronModel.min] = minute;
      break;
    case frequency.EVERY_6_HOURS:
    case frequency.EVERY_12_HOURS:
      // TODO: add min and hour to support min and hour offsets
      // [cronModel.min] = minute;
      // [cronModel.hour] = hour;
      break;
    default:
      throw new Error(`Unknown frequency: ${cronModel.frequency}`);
  }

  return cronModel;
}

/**
 * Converts a cron model into a cron string.
 *
 * @param {Object} cronModel - a model which is the result of cron.parse().
 * @returns {String} a raw cron string.
 */
export function toCronString(cronModel) {
  const defaultCronModel = {
    min: '0',
    hour: '*',
    dayOfWeek: '*',
    dayOfMonth: '*',
    month: '*',
    year: '*',
  };

  const finalCronModel = _.defaults(cronModel, defaultCronModel);

  if (finalCronModel.frequency === frequency.EVERY_6_HOURS ||
    finalCronModel.frequency === frequency.EVERY_12_HOURS) {
    const startHour = finalCronModel.hour ? finalCronModel.hour : '*';
    const interval = finalCronModel.frequency === frequency.EVERY_6_HOURS ?
      6 : 12;
    finalCronModel.hour = `${startHour}/${interval}`;
  }

  return curlyTemplate('{{min}} {{hour}} {{dayOfMonth}} {{month}} {{dayOfWeek}} {{year}}')(finalCronModel);
}
